import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import React from 'react';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { HttpRequest } from 'util/http';
import Button from 'reactstrap/lib/Button';
import './styles.css';
import { userData } from 'util/userData';
import Spinner from 'reactstrap/lib/Spinner';
import Alert from 'reactstrap/lib/Alert';
import deleteButton from './deleteButton';
import AddSegmentModal from "./addSegmentModal";

export interface Segment {
  id: number;
  customer: string;
  filecolumnname: string;
  segmentavid: string;
  segmentname: string;
  segmentvalueavid: string;
  segmentvaluename: string;
  startvalue: string;
  include_in_deck: number;
}

export function SegmentsGrid() {
  const [segments, setSegments] = React.useState<Object[]>();
  const [gettingDataError, setGettingDataError] = React.useState<Error>();
  const [callingTheApi, setCallingTheApi] = React.useState<boolean>(false);
  const [apiCallSuccess, setApiCallSuccess] = React.useState<boolean>(true);
  const [addSegmentModalOpen, setAddSegmentModalOpen] = React.useState<boolean>(false);
  const defaultColDefs = {
    editable: true,
    sortable: true,
    filter: true,
    resizable: true,
  }
  const columnDefs: ColDef[] = [
    { field: 'id', editable: false },
    { field: 'customer', editable: false },
    { field: 'filecolumnname' },
    { field: 'segmentavid' },
    { field: 'segmentname' },
    { field: 'segmentvalueavid' },
    { field: 'segmentvaluename' },
    { field: 'startvalue' },
    { field: 'include_in_deck' },
    { headerName: 'Delete', cellRenderer: deleteButton }
  ]

  React.useEffect(() => {
    const getSegmentsByCustomerName = () => {
      const method = 'POST'
      const data = { name: userData.getCurrentProject() } // get the current bank name
      setCallingTheApi(true);
      setApiCallSuccess(false);
      HttpRequest.fetch('/web/v1/segments/get-segments-by-customer-name', { method, data }).then((res: any) => {
        if (res.data) {
          setSegments(res.data.segments);
          setCallingTheApi(false);
        } else {
          setGettingDataError(res.data.error);
          setCallingTheApi(false);
        }
      });
    }
    getSegmentsByCustomerName();
  }, []);

  const deleteRow = (id: number) => {
    const method = 'POST'
    const data = { id }
    setCallingTheApi(true);
    HttpRequest.fetch('/web/v1/segments/delete-segment', { method, data })
      .then((res: any) => {
        if (res.data.success) {
          let newSegments = [...segments];
          newSegments = newSegments.filter((segment: Segment) => segment.id !== id);
          setSegments(newSegments);
          setCallingTheApi(false);
          setApiCallSuccess(true)
        } else {
          setGettingDataError(res.data.error);
          setCallingTheApi(false);
        }
      });
  }

  const onCellClicked = (params: any) => {
    if (params.colDef.headerName === 'Delete') {
      deleteRow(params.data.id);
    }
  }

  const onFirstDataRendered = (params: any) => {
    params.api.sizeColumnsToFit();
  }

  const addNewRow = () => {
    let newSegments = [...segments];
    newSegments.unshift({ customer: userData.getCurrentProject() }); // Add new row
    setSegments(newSegments);
  }

  const handleOnCellValueChanged = (rowData: Segment) => {
    if (rowData.id === undefined) {
      // If the rowData doesn't have an id, it means it's a new row
      // we add it if the entire row has been filled out
      let unfilledOut = false;
      for (const key in rowData) {
        if (rowData[key] === undefined || rowData[key] === '') {
          if (key !== 'id') {
            unfilledOut = true;
            break;
          } else {
            break;
          }
        }
      }
      addSegment(rowData);
      return;
    } else if (typeof rowData.id === 'number') {
      // If the rowData has an id, it means it's an existing row, so we patch it
      patchSegment(rowData);
    }
  }

  const patchSegment = (rowData: Segment) => {
    const method = 'POST'
    const data = { ...rowData }
    setCallingTheApi(true);
    setGettingDataError(null);
    setApiCallSuccess(false);
    HttpRequest.fetch('/web/v1/segments/patch-segment', { data, method })
      .then((res: any) => {
        if (!res.data.error) {
          let newSegments = [...segments];
          newSegments = newSegments.map((segment: Segment) => {
            if (segment.id === rowData.id) {
              return rowData;
            }
            return segment;
          });
          setSegments(newSegments);
          setCallingTheApi(false);
          setApiCallSuccess(true)
        } else {
          setGettingDataError(res.data.error);
          setCallingTheApi(false);
        }
      });
  }

  const addSegment = (rowData: Segment) => {
    const method = 'POST'
    const data = { ...rowData, operator: 'EQUALS' }
    setCallingTheApi(true);
    setGettingDataError(null);
    setApiCallSuccess(false);
    HttpRequest.fetch('/web/v1/segments/add-segment', { method, data }).then((res: any) => {
      if (!res.data.error) {
        setSegments([...segments, res.data]);
        setCallingTheApi(false);
        setApiCallSuccess(true)
      } else {
        setGettingDataError(res.data.error);
        setCallingTheApi(false);
      }
    });
  }


  return (
    <>
      <AddSegmentModal
        isOpen={addSegmentModalOpen}
        toggle={() => setAddSegmentModalOpen(!addSegmentModalOpen)}
        addSegment={addSegment}
      />
      <div className='segments-grid-header'>
        <h1>Segments</h1>
        {gettingDataError && <Alert color='warning'>There was an error: {gettingDataError.message}</Alert>}
        {callingTheApi &&
          <Alert color="primary" className='segments-grid-api-call-alert'>
            <p>Calling the api...</p>
            <Spinner />
          </Alert>
        }
        {apiCallSuccess &&
          <Alert>Successfully saved changes</Alert>
        }
        <Button onClick={() => setAddSegmentModalOpen(true)}>Add New Segment</Button>
      </div>
      <div className="ag-theme-balham default-grid-size">
        <AgGridReact
          rowData={segments}
          columnDefs={columnDefs}
          onFirstDataRendered={onFirstDataRendered}
          defaultColDef={defaultColDefs}
          onCellValueChanged={(e) => handleOnCellValueChanged(e.data)}
          onCellClicked={onCellClicked}
          rowHeight={40}
        />
      </div>
    </>
  )
}